import type { AttrDef, DisplayTemplateRule } from './moving-item.types';

export function newDisplayTemplateRowKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `dt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function firstKeyedAttrKey(attributes: AttrDef[]): string {
  const a = attributes.find((x) => x.key.trim());
  return a?.key.trim() ?? '';
}

export function createAlwaysDisplayTemplateRule(template = '{displayName}'): DisplayTemplateRule {
  return {
    rule: { type: 'ALWAYS_MATCH' },
    template,
    _clientRowKey: newDisplayTemplateRowKey(),
  };
}

export function createConditionalDisplayTemplateRule(attributes: AttrDef[]): DisplayTemplateRule {
  const key = firstKeyedAttrKey(attributes);
  return {
    rule: {
      type: 'CROSS_AND',
      clauses: key
        ? [{ key, rule: { type: 'ALWAYS_MATCH' } }]
        : [{ key: '', rule: { type: 'ALWAYS_MATCH' } }],
    },
    template: '',
    _clientRowKey: newDisplayTemplateRowKey(),
  };
}

/** 조건부 규칙 추가: 마지막이 ALWAYS면 그 앞에 삽입, 아니면 [조건, ALWAYS] 시드 */
export function addDisplayTemplateRuleRow(
  rules: DisplayTemplateRule[],
  attributes: AttrDef[]
): DisplayTemplateRule[] {
  if (rules.length === 0) {
    return [createConditionalDisplayTemplateRule(attributes), createAlwaysDisplayTemplateRule()];
  }
  const last = rules[rules.length - 1];
  if (last.rule.type === 'ALWAYS_MATCH') {
    const cond = createConditionalDisplayTemplateRule(attributes);
    return [...rules.slice(0, -1), cond, last];
  }
  return [
    ...rules,
    createConditionalDisplayTemplateRule(attributes),
    createAlwaysDisplayTemplateRule(),
  ];
}

export function removeDisplayTemplateRuleAt(
  rules: DisplayTemplateRule[],
  index: number
): DisplayTemplateRule[] {
  return rules.filter((_, i) => i !== index);
}

export function moveDisplayTemplateRule(
  rules: DisplayTemplateRule[],
  index: number,
  delta: -1 | 1
): DisplayTemplateRule[] {
  const j = index + delta;
  if (j < 0 || j >= rules.length) return rules;
  const next = [...rules];
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

export function updateDisplayTemplateRuleAt(
  rules: DisplayTemplateRule[],
  index: number,
  patch: Partial<Pick<DisplayTemplateRule, 'rule' | 'template'>>
): DisplayTemplateRule[] {
  return rules.map((r, i) => (i === index ? { ...r, ...patch } : r));
}
